import React from 'react'
import { inject, observer } from 'mobx-react'
import { Button, Form, Input, Modal, Radio } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import { users, agency } from '@services/api'
import { alertPopup } from '@utils/popup'

interface IClientInfo {
    id: number | string
    first_name?: string
    last_name?: string
    identity?: string
    phone?: string
    email?: string
    agency_id?: string
    is_collective?: boolean
    sto_id?: string
    sto_status?: string
    terminal_name?: string
    [key: string]: any
}

interface IProps extends FormComponentProps {
    clientInfo: IClientInfo
    updateInfo: () => void
    roles: string[]
    popupStore?: any
}

const ERRORS = {
    USER_NOT_FOUND: 'משתמש לא נמצא',
    UPDATE_USER_ERROR: 'שגיאה בעדכון פרטי משתמש',
    UPDATE_INVITATION_ERROR: 'שגיאה בעדכון invitation',
    CRM_ERROR: 'שגיאת CRM',
    IDENTITY_EXIST: 'ת״ז כבר קיים במערכת',
    PHONE_EXIST: 'מספר טלפון כבר קיים במערכת',
    EMAIL_EXIST: 'מייל כבר קיים במערכת',
    UPDATE_COLLECTIVE_ERROR: 'שגיאה בעדכון נתוני הו״ק'
}

@inject((stores: any) => {
    const { popupStore } = stores
    return { popupStore }
})
@observer
class EditAgentInfo extends React.Component<IProps, any> {
    _isMounted = false
    _prevPopupType = ''

    // ── validators ───────────────────────────────────────────
    validators = {
        phone: async phone => {
            if (this.toApiPhone(phone) === this.props.clientInfo.phone) return { text: '', isError: false }
            if (phone.length === 10) {
                const apiPhone = this.toApiPhone(phone)
                return users
                    .checkUnique({ phone: apiPhone })
                    .then(() => ({ text: '', isError: false }))
                    .catch(() => ({ text: 'קיים במערכת', isError: true }))
            } else if (phone.length > 10) return { text: 'לא תקין', isError: true }
            else return { text: '', isError: false }
        },

        email: async email => {
            if (email === this.props.clientInfo.email || !email) return { text: '', isError: false }
            return users
                .checkUnique({ email })
                .then(() => ({ text: '', isError: false }))
                .catch(() => ({ text: 'קיים במערכת', isError: true }))
        },

        agency_id: async agencyId => {
            if ((agencyId + '').length === 0) return { text: '', isError: false }
            return agency
                .getAgency({ license: agencyId })
                .then(res => ({ text: res.data.name, isError: false }))
                .catch(() => ({ text: 'רשיון לא נמצא', isError: true }))
        }
    }

    fieldRules = {
        first_name: [{ required: true, message: 'שדה חובה' }],
        last_name: [{ required: true, message: 'שדה חובה' }],
        identity: [
            { required: true, message: 'שדה חובה' },
            { pattern: /^\d{9}$/, message: 'תעודת זהות לא תקינה' }
        ],
        phone: [
            { required: true, message: 'שדה חובה' },
            { pattern: /^0\d{9}$/, message: 'טלפון לא תקין' }
        ],
        email: [
            { required: true, message: 'שדה חובה' },
            { type: 'email', message: 'מייל לא תקין' }
        ]
    }

    constructor(props) {
        super(props)
        this.state = {
            messages: {
                phone: { text: '', isError: false },
                email: { text: '', isError: false },
                agency_id: { text: '', isError: false }
            },
            isCollective: false
        }
    }

    // ── lifecycle ────────────────────────────────────────────
    componentDidMount() {
        this._isMounted = true
        this.setFields()
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    componentDidUpdate(prevProps) {
        // Decorated fields only register once the Modal mounts (popup opens).
        // Repopulate on the open transition so values aren't lost to rc-form buffering.
        const { popupType } = this.props.popupStore
        if (popupType === 'editAgentInfo' && this._prevPopupType !== 'editAgentInfo') {
            this.setFields()
        }
        this._prevPopupType = popupType

        if (prevProps.clientInfo !== this.props.clientInfo && this.props.clientInfo && this.props.clientInfo.id) {
            this.setFields()
        }
    }

    // ── phone normalization ──────────────────────────────────
    toDisplayPhone = phone => (phone ? '0' + phone : '')

    toApiPhone = phone => (phone && phone.length === 10 && phone[0] === '0' ? phone.substring(1) : phone)

    getRoleFlags = () => {
        const roles = this.props.roles || []
        return {
            canEditAll: roles.includes('admin'),
            canEditCollective: roles.includes('admin') || roles.includes('money')
        }
    }

    // ── form ─────────────────────────────────────────────────
    normalizeField = (field, value) => {
        if (value == null) return value
        if (field === 'identity' || field === 'phone' || field === 'agency_id') {
            return String(value).replace(/[^0-9]/g, '')
        }
        return value
    }

    setFields = () => {
        const { clientInfo, popupStore } = this.props
        const { canEditAll, canEditCollective } = this.getRoleFlags()
        if (!clientInfo || !clientInfo.id) return
        // Decorated fields exist only while the Modal is open. Setting values before
        // they register triggers rc-form's "cannot set a form field before rendering" warning.
        if (popupStore.popupType !== 'editAgentInfo') return

        if (canEditAll) {
            this.props.form.setFieldsValue({
                first_name: clientInfo.first_name,
                last_name: clientInfo.last_name,
                identity: clientInfo.identity,
                phone: this.toDisplayPhone(clientInfo.phone),
                email: clientInfo.email,
                agency_id: clientInfo.agency_id
            })
        }

        const isCollective = !!clientInfo.is_collective
        this.setState({ isCollective }, () => {
            // The collective fields render conditionally — set their values only
            // after the re-render registers them with rc-form (else lost to buffering).
            if (!isCollective || !canEditCollective) return
            const collectiveValues: any = {}
            if (clientInfo.sto_id) collectiveValues.sto_id = clientInfo.sto_id
            if (clientInfo.sto_status) collectiveValues.sto_status = clientInfo.sto_status
            if (clientInfo.terminal_name) collectiveValues.terminal_name = clientInfo.terminal_name
            this.props.form.setFieldsValue(collectiveValues)
        })
    }

    onClose = () => {
        this.setState({
            messages: {
                phone: { text: '', isError: false },
                email: { text: '', isError: false },
                agency_id: { text: '', isError: false }
            }
        })
        this.props.popupStore.togglePopup(false)
    }

    handleFieldChange = e => {
        const field = e.target.id.split('-')[1]
        const value = this.normalizeField(field, e.target.value)

        if (!this.validators[field]) return
        this.validators[field](value).then(res => {
            if (!this._isMounted) return
            this.setState(prev => ({ messages: { ...prev.messages, [field]: res } }))
        })
    }

    cancel = field => {
        const { clientInfo } = this.props
        const value = field === 'phone' ? this.toDisplayPhone(clientInfo.phone) : clientInfo[field]
        this.props.form.setFields({ [field]: { value, errors: undefined } })
        this.setState(prev =>
            field in prev.messages ? { messages: { ...prev.messages, [field]: { text: '', isError: false } } } : null
        )
    }

    // ── submit ───────────────────────────────────────────────
    submit = () => {
        this.props.form.validateFields((err, values) => {
            if (err) return

            const { id } = this.props.clientInfo
            const { canEditAll, canEditCollective } = this.getRoleFlags()
            const payload: any = { id }

            if (canEditAll) {
                payload.first_name = values.first_name
                payload.last_name = values.last_name
                payload.identity = values.identity
                payload.phone = this.toApiPhone(values.phone)
                payload.email = values.email
                payload.agency_id = values.agency_id
            }

            if (canEditCollective && this.state.isCollective) {
                payload.sto_id = values.sto_id
                payload.sto_status = values.sto_status
                payload.terminal_name = values.terminal_name
            }

            users
                .editAgentInfo(payload)
                .then(() => {
                    this.props.updateInfo()
                    this.onClose()
                    alertPopup({ type: 'success', title: 'העדכון בוצע בהצלחה', body: '' })
                })
                .catch(error => {
                    const res = error.response
                    let body = <div />
                    if (res && res.data && Array.isArray(res.data.errorArray)) {
                        body = (
                            <div>
                                {res.data.errorArray.map((e, idx) => (
                                    <p key={idx.toString()}>{ERRORS[e.errorCode] || e.errorCode}</p>
                                ))}
                            </div>
                        )
                    }

                    alertPopup({ type: 'error', title: 'אירעה שגיאה. העדכון לא בוצע / לא בוצע באופן מלא', body })
                    this.props.updateInfo()
                })
        })
    }

    renderInputRow = ({ field, label, message = null }) => {
        const { getFieldDecorator, getFieldError } = this.props.form
        const ruleError = (getFieldError(field) || [])[0]
        const shown = ruleError ? { text: ruleError, isError: true } : message

        return (
            <div style={{ ...style.row }}>
                <span style={{ ...style.label2 }}>{label}</span>
                <div style={{ ...style.inputCell }}>
                    {getFieldDecorator<any>(field, {
                        normalize: v => this.normalizeField(field, v),
                        rules: this.fieldRules[field]
                    })(
                        <Input
                            style={{ ...style.fieldInput }}
                            id={`input-${field}`}
                            onChange={this.handleFieldChange}
                        />
                    )}
                    {shown && shown.text && (
                        <div style={{ ...style.box, ...style.messageBox, color: shown.isError ? 'red' : '#389e0d' }}>
                            {shown.text}
                        </div>
                    )}
                    <div onClick={() => this.cancel(field)} style={{ ...style.box, ...style.leftBox }}>
                        {'איפוס'}
                    </div>
                </div>
            </div>
        )
    }

    renderCollectiveRow = () => {
        const { getFieldDecorator } = this.props.form

        return (
            <div style={{ ...style.inlineRow }}>
                <div style={{ ...style.inlineInputGroup }}>
                    <span style={{ ...style.label2 }}>{'מזהה חיוב:'}</span>
                    <div style={{ ...style.inputCell }}>
                        {getFieldDecorator<any>('sto_id', {
                            normalize: v => this.normalizeField('sto_id', v)
                        })(
                            <Input
                                style={{ ...style.fieldInput }}
                                id="input-sto_id"
                                onChange={this.handleFieldChange}
                            />
                        )}
                        <div onClick={() => this.cancel('sto_id')} style={{ ...style.box, ...style.leftBox }}>
                            {'איפוס'}
                        </div>
                    </div>
                </div>
                <div style={{ ...style.inlineRadioGroup }}>
                    <span style={{ ...style.label2 }}>{'סטטוס:'}</span>
                    {getFieldDecorator<any>('sto_status')(
                        <Radio.Group>
                            <Radio value="active">{'פעיל'}</Radio>
                            <Radio value="inactive">{'לא פעיל'}</Radio>
                        </Radio.Group>
                    )}
                </div>
                <div style={{ ...style.inlineRadioGroup }}>
                    <span style={{ ...style.label2 }}>{'מסוף:'}</span>
                    {getFieldDecorator<any>('terminal_name')(
                        <Radio.Group>
                            <Radio value="mediwho1tok">{'אשראי'}</Radio>
                            <Radio value="mediwhobk">{'הו״ק'}</Radio>
                        </Radio.Group>
                    )}
                </div>
            </div>
        )
    }

    // ── render ───────────────────────────────────────────────
    render() {
        const {
            popupStore: { togglePopup, popupType }
        } = this.props
        const {
            messages: { phone: phoneMessage, email: emailMessage, agency_id: agencyIdMessage },
            isCollective
        } = this.state
        const { canEditAll, canEditCollective } = this.getRoleFlags()

        return (
            <>
                <Button style={{ marginLeft: '0px' }} onClick={() => togglePopup('editAgentInfo')}>
                    {'עריכת פרטים'}
                </Button>
                {popupType === 'editAgentInfo' && (
                    <Modal
                        width={'700px'}
                        onCancel={this.onClose}
                        visible={true}
                        title={'עריכת פרטי משתמש'}
                        footer={null}
                    >
                        <div style={{ ...style.content }}>
                            {canEditAll && (
                                <>
                                    {this.renderInputRow({ field: 'first_name', label: 'שם פרטי:' })}
                                    {this.renderInputRow({ field: 'last_name', label: 'שם משפחה:' })}
                                    {this.renderInputRow({ field: 'identity', label: 'מספר זהות:' })}
                                    {this.renderInputRow({ field: 'phone', label: 'טלפון:', message: phoneMessage })}
                                    {this.renderInputRow({ field: 'email', label: 'מייל:', message: emailMessage })}
                                    {this.renderInputRow({
                                        field: 'agency_id',
                                        label: 'מזהה סוכנות:',
                                        message: agencyIdMessage
                                    })}
                                </>
                            )}
                            {canEditCollective && isCollective && this.renderCollectiveRow()}
                            <Button style={{ alignSelf: 'center', marginTop: '20px' }} onClick={this.submit}>
                                {'שמירה'}
                            </Button>
                        </div>
                    </Modal>
                )}
            </>
        )
    }
}

const style = {
    content: { display: 'flex', flexDirection: 'column' as 'column', gap: '12px' },
    row: { display: 'flex', flexDirection: 'column' as 'column' },
    inputCell: { display: 'flex', alignItems: 'center', width: '100%' },
    inlineRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' },
    inlineInputGroup: { display: 'flex', flex: 1, flexDirection: 'column' as 'column', minWidth: 0 },
    inlineRadioGroup: { display: 'flex', flexDirection: 'column' as 'column' },
    label2: { marginBottom: '4px' },
    box: {
        background: 'white',
        border: 'solid 1px #d1d1d1',
        height: '32px',
        paddingInline: '15px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0.3rem',
        cursor: 'pointer'
    },
    fieldInput: { flex: 1, borderRadius: '0 4px 4px 0' },
    leftBox: { borderRadius: '4px 0 0 4px' },
    messageBox: {
        borderRight: 'none',
        lineHeight: '1',
        fontSize: '12px',
        width: '150px',
        overflowY: 'auto' as 'auto'
    }
}

export default Form.create<IProps>()(EditAgentInfo)
